"use client";

import { useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { Box, Step, StepLabel, Stepper } from "@mui/material";

import { useOpenAccount } from "@/context/openAccount";
import { useEmailApi } from "@/context/openAccount/email";
import { useUi } from "@/context/util/ui";
import { useRecaptcha } from "@/context/util/recaptcha";

import LoginButton from "@/components/loginButton";
import AcceptButton from "@/components/openAccount/accept/button";
import AcceptForm from "@/components/openAccount/accept/form";
import SetupButton from "@/components/openAccount/setup/button";
import SetupForm from "@/components/openAccount/setup/form";
import VerifyButton from "@/components/openAccount/verify/button";
import VerifyForm from "@/components/openAccount/verify/form";

export default function OpenAccount() {
  const { alerts, setLoading } = useUi();
  const { executeRecaptcha } = useRecaptcha();
  const { activeStep, openAccountUser, setActiveStep } = useOpenAccount();
  const { validateEmailToken } = useEmailApi();

  const searchParams = useSearchParams();

  const token = searchParams.get("token");

  useEffect(() => {
    // wait for execute recaptcha no matter what
    if (
      activeStep === -1 &&
      (!openAccountUser || !openAccountUser.verified) &&
      executeRecaptcha &&
      token
    )
      // validate token if it exists and step not set
      validateEmailToken(token ?? "");
    else if (executeRecaptcha && activeStep === -1) {
      // otherwise set the active step if there is no token param
      setActiveStep(0);
      setLoading(false);
    }
  }, [activeStep, executeRecaptcha, openAccountUser, token]);

  function getContent() {
    switch (activeStep) {
      case 0:
        return (
          <>
            <AcceptForm />
            <AcceptButton />
          </>
        );
      case 1:
        return (
          <>
            <VerifyForm />
            <VerifyButton />
          </>
        );
      case 2:
        return (
          <>
            <SetupForm />
            <SetupButton />
          </>
        );
      case 3:
        return <LoginButton />;
      default:
        return null;
    }
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Stepper activeStep={activeStep}>
        <Step completed={activeStep > 0}>
          <StepLabel>Accept</StepLabel>
        </Step>
        <Step completed={activeStep > 1}>
          <StepLabel>Verify</StepLabel>
        </Step>
        <Step completed={activeStep > 2}>
          <StepLabel>Setup</StepLabel>
        </Step>
      </Stepper>
      <Box
        sx={{
          mb: 2,
          mt: 2,
          width: "100%",
        }}
      >
        {alerts?.get(activeStep)}
        {getContent()}
      </Box>
    </Box>
  );
}
